/**
 * @module ui.list-sort
 * @version 5.0.0
 * @description Sort controls Web Component for liftr-list
 * 
 * Supports dropdown select and button triggers for sorting.
 * Emits sort changes to parent list.
 * 
 * @element liftr-list-sort
 * 
 * @fires liftr:list-sort:changed - Sort selection changed
 * 
 * @attr {string} liftr-target - Required: liftr-id of target list
 * @attr {string} liftr-state - Output: {key} sort-asc | {key} sort-desc
 * 
 * CSS Settings:
 * - --list-sort-label: "field" | "label" (default: "label")
 *   Controls the text source for sort-label element.
 *   "label" = trigger's textContent, "field" = trigger's liftr-field attribute.
 * 
 * @changelog
 * - 5.0.0: Added sort-clear role for resetting to default collection order
 *        - New ROLES.CLEAR constant
 *        - Clear trigger calls this.clear() on click
 *        - Active state on clear trigger when no sort is active
 * - 4.9.0: CSS Setting --list-sort-label for label text source
 *        - "label" (default): uses trigger textContent
 *        - "field": uses trigger liftr-field attribute (with textContent fallback)
 * - 4.8.0: BREAKING - Removed liftr-field on host and triggers
 *        - Key token derived from liftr-value (part before ":")
 *        - Host state: liftr-state="{key} sort-{dir}" e.g. "bestseller sort-desc"
 *        - state.clear() replaces individual remove calls
 * - 4.7.0: Host liftr-field propagation for per-field CSS icons
 *        - Smart toggle: only toggles direction when counterpart trigger exists
 *        - Single triggers (e.g. "sales:desc") stay fixed
 * - 4.6.0: Host state (sort-asc/sort-desc) on custom element
 *        - Added sort-label role for dynamic label text
 *        - Label text from liftr-field attribute on triggers
 * - 4.5.0: AUDIT COMPLIANCE - Quality Checks v4.8
 *        - Added ROLES, STATES, EVENTS, SETTINGS, LISTEN constants (frozen)
 *        - All string literals → constants
 *        - Correct named export pattern
 * - 4.1.0: BREAKING - Finsweet-style toggle behavior for buttons
 * - 4.0.0: Initial v4.0 implementation
 * 
 * @example Select Dropdown
 * <liftr-list-sort liftr-target="store-list">
 *   <select liftr-role="sort-select">
 *     <option value="">Standard</option>
 *     <option value="name:asc">Name A-Z</option>
 *     <option value="name:desc">Name Z-A</option>
 *     <option value="distance:asc">Nearest First</option>
 *   </select>
 * </liftr-list-sort>
 * 
 * @example Button Triggers with Label and Clear
 * <liftr-list-sort liftr-target="store-list">
 *   <button liftr-role="panel-trigger">
 *     <span liftr-role="sort-label">Sortierung</span>
 *   </button>
 *   <button liftr-role="sort-clear">Standard</button>
 *   <button liftr-role="sort-trigger" liftr-value="name:asc" liftr-field="Name">Name A-Z</button>
 *   <button liftr-role="sort-trigger" liftr-value="name:desc" liftr-field="Name">Name Z-A</button>
 *   <button liftr-role="sort-trigger" liftr-value="price:asc" liftr-field="Preis">Preis aufsteigend</button>
 *   <button liftr-role="sort-trigger" liftr-value="price:desc" liftr-field="Preis">Preis absteigend</button>
 *   <button liftr-role="sort-trigger" liftr-value="bestseller:desc" liftr-field="Beliebt">Beliebt</button>
 * </liftr-list-sort>
 * 
 * @example CSS Settings
 * .shop_list-sort {
 *   --list-sort-label: label;  // Default: trigger textContent
 *   --list-sort-label: field;  // Use liftr-field attribute
 * }
 * 
 * Host states (all via liftr-state, key from liftr-value before ":"):
 * - liftr-state="sort-name sort-asc" when sorting by name ascending
 * - liftr-state="sort-name sort-desc" when sorting by name descending
 * - liftr-state="sort-bestseller sort-desc" when sorting by bestseller
 * - No state when inactive
 * 
 * CSS targeting:
 * - liftr-list-sort[liftr-state~="sort-asc"] → arrow up icon
 * - liftr-list-sort[liftr-state~="sort-desc"] → arrow down icon
 * - liftr-list-sort[liftr-state~="sort-bestseller"] → star icon override
 * 
 * Button states:
 * - liftr-state="active sort-asc" when sorting ascending
 * - liftr-state="active sort-desc" when sorting descending
 * - No state when inactive
 * 
 * Clear trigger states:
 * - liftr-state="active" when no sort is active (default order)
 * - No state when a sort is active
 * 
 * Toggle behavior:
 * - Paired triggers (name:asc + name:desc) → toggle on repeat click
 * - Single triggers (sales:desc) → no toggle, direction stays fixed
 */

import { LiftrModule } from './core.module.js';
import { ATTRIBUTES, on } from './core.liftr.js';

/* ═══════════════════════════════════════════════════════════════════
 * module constants
 * ═══════════════════════════════════════════════════════════════════ */

/** Module-specific attribute for sort value on triggers */
const ATTR_VALUE = 'liftr-value';

/**
 * Role constants for DOM queries.
 * @type {Readonly<Record<string, string>>}
 */
const ROLES = Object.freeze({
  SELECT:  'sort-select',
  TRIGGER: 'sort-trigger',
  CLEAR:   'sort-clear',
  LABEL:   'sort-label'
});

/**
 * State tokens for liftr-state attribute.
 * Applied to host element and individual trigger buttons.
 * Note: Key tokens (e.g. "sort-bestseller", "sort-name") are dynamic from liftr-value.
 * @type {Readonly<Record<string, string>>}
 */
const STATES = Object.freeze({
  SORT_ASC:  'sort-asc',
  SORT_DESC: 'sort-desc',
  ACTIVE:    'active'
});

/**
 * Event action names (auto-prefixed with liftr:list-sort:).
 * @type {Readonly<Record<string, string>>}
 */
const EVENTS = Object.freeze({
  CHANGED: 'changed'
});

/**
 * Settings tokens for liftr-settings attribute.
 * @type {Readonly<Record<string, string>>}
 */
const SETTINGS = Object.freeze({
  // Reserved for future use
});

/**
 * External events this module listens to.
 * NOT exported (internal wiring only).
 * @type {Readonly<Record<string, string>>}
 */
const LISTEN = Object.freeze({
  // Reserved for future use
});

/**
 * CSS schema for watchSettings() (internal).
 * --list-sort-label: "field" | "label"
 * @private
 */
const CSS_SCHEMA = Object.freeze({
  label: { type: 'string', default: 'label' }
});

/* ═══════════════════════════════════════════════════════════════════
 * list sort web component
 * ═══════════════════════════════════════════════════════════════════ */

class LiftrListSort extends LiftrModule {
  
  static NAME = 'list-sort';
  
  static DEFAULTS = {};
  
  /* ═══════════════════════════════════════════════════════════════════
   * private state
   * ═══════════════════════════════════════════════════════════════════ */
  
  /** @type {HTMLSelectElement|null} Sort select dropdown */
  #select = null;
  
  /** @type {HTMLButtonElement[]} Sort trigger buttons */
  #triggers = [];
  
  /** @type {HTMLButtonElement|null} Clear trigger button */
  #clearTrigger = null;
  
  /** @type {HTMLElement|null} Sort label element */
  #label = null;
  
  /** @type {string|null} Target list ID */
  #targetId = null;
  
  /** @type {string|null} Currently active sort key */
  #activeKey = null;
  
  /** @type {'asc'|'desc'} Currently active sort direction */
  #activeDirection = 'asc';
  
  /** @type {string} Label source: 'field' or 'label' */
  #labelSource = 'label';
  
  
  /* ═══════════════════════════════════════════════════════════════════
   * lifecycle
   * ═══════════════════════════════════════════════════════════════════ */
  
  mount() {
    // Read target
    this.#targetId = this.getAttribute(ATTRIBUTES.TARGET);
    
    if (!this.#targetId) {
      return;
    }
    
    // Read label source CSS setting
    this.#labelSource = getComputedStyle(this)
      .getPropertyValue('--list-sort-label').trim() || 'label';
    
    // Find select dropdown
    this.#select = this.role(ROLES.SELECT);
    
    // Find trigger buttons
    this.#triggers = this.roleAll(ROLES.TRIGGER);
    
    // Find clear trigger
    this.#clearTrigger = this.role(ROLES.CLEAR);
    
    // Find and preserve label default text
    this.#label = this.role(ROLES.LABEL);
    if (this.#label && !this.#label.dataset.default) {
      this.#label.dataset.default = this.#label.textContent;
    }
    
    // Setup select listener
    if (this.#select) {
      const handler = this.#handleSelectChange.bind(this);
      this.#select.addEventListener('change', handler);
      this.addDisposable(() => this.#select?.removeEventListener('change', handler));
    }
    
    // Setup trigger listeners
    this.#triggers.forEach(trigger => {
      const handler = this.#handleTriggerClick.bind(this);
      trigger.addEventListener('click', handler);
      this.addDisposable(() => trigger.removeEventListener('click', handler));
    });
    
    // Setup clear trigger listener
    if (this.#clearTrigger) {
      const handler = this.#handleClearClick.bind(this);
      this.#clearTrigger.addEventListener('click', handler);
      this.addDisposable(() => this.#clearTrigger?.removeEventListener('click', handler));
    }

  }
  
  unmount() {
    this.#select = null;
    this.#triggers = [];
    this.#clearTrigger = null;
    this.#label = null;
    this.#targetId = null;
    this.#activeKey = null;
    this.#labelSource = 'label';
    super.unmount();
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * event handlers
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Handle list cleared event
   * @param {CustomEvent} event
   * @private
   */
  #handleListCleared(event) {
    const { listId } = event.detail;
    
    // Only reset if this is our target list
    if (listId !== this.#targetId) return;
    
    this.clear();
  }
  
  /**
   * Handle select dropdown change
   * @param {Event} event
   * @private
   */
  #handleSelectChange(event) {
    const select = event.target;
    const value = select.value;
    
    if (!value) {
      // Reset sort
      this.#activeKey = null;
      this.#activeDirection = 'asc';
    } else {
      // Parse value format: "key:direction" or just "key"
      const [key, direction = 'asc'] = value.split(':');
      this.#activeKey = key;
      this.#activeDirection = direction === 'desc' ? 'desc' : 'asc';
    }
    
    this.#updateStates();
    this.#emitCurrentState();
  }
  
  /**
   * Handle clear trigger click — reset to default collection order.
   * @param {Event} _event
   * @private
   */
  #handleClearClick(_event) {
    this.clear();
  }
  
  /**
   * Handle trigger button click
   * 
   * Behavior:
   * - Different key: Activate with initial direction (from value or asc)
   * - Same key + counterpart exists: Toggle direction (asc ↔ desc)
   * - Same key + no counterpart: No change (single sort like "popular")
   * 
   * @param {Event} event
   * @private
   */
  #handleTriggerClick(event) {
    const trigger = event.currentTarget;
    const value = trigger.getAttribute(ATTR_VALUE) || '';
    
    // Parse value format: "key:direction" or just "key"
    const [key, initialDirection = 'asc'] = value.split(':');
    
    if (!key) return;
    
    // Check if clicking same sort key
    const isSameKey = this.#activeKey === key;
    
    if (isSameKey) {
      // Only toggle if a counterpart trigger exists
      const opposite = this.#activeDirection === 'asc' ? 'desc' : 'asc';
      const hasCounterpart = this.#triggers.some(t => {
        const v = t.getAttribute(ATTR_VALUE) || '';
        return v === `${key}:${opposite}` || v === key;
      });
      
      if (hasCounterpart) {
        this.#activeDirection = opposite;
      }
    } else {
      // New key: use initial direction from value
      this.#activeKey = key;
      this.#activeDirection = initialDirection === 'desc' ? 'desc' : 'asc';
    }
    
    this.#updateStates();
    this.#emitCurrentState();
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * state management
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Find the active trigger match (exact value:direction, then key-only fallback)
   * @returns {HTMLButtonElement|undefined}
   * @private
   */
  #getActiveMatch() {
    if (!this.#activeKey) return undefined;
    
    const exactValue = `${this.#activeKey}:${this.#activeDirection}`;
    
    return this.#triggers.find(t => t.getAttribute(ATTR_VALUE) === exactValue)
      || this.#triggers.find(t => {
        const [key] = (t.getAttribute(ATTR_VALUE) || '').split(':');
        return key === this.#activeKey;
      });
  }
  
  /**
   * Resolve label text from active trigger.
   * Source controlled by --list-sort-label CSS setting:
   * - "field": liftr-field attribute (fallback to textContent)
   * - "label": textContent (default)
   * 
   * @param {HTMLButtonElement|undefined} match - Active trigger element
   * @returns {string}
   * @private
   */
  #resolveLabelText(match) {
    if (!match) return '';
    
    if (this.#labelSource === 'field') {
      return match.getAttribute(ATTRIBUTES.FIELD) || match.textContent || '';
    }
    
    return match.textContent || '';
  }
  
  /**
   * Update sort state on host, trigger buttons, clear trigger, and label.
   * 
   * Host receives two state categories:
   * - Direction: sort-asc | sort-desc
   * - Key token: sort-{key} from liftr-value before ":" (e.g. "sort-bestseller", "sort-name")
   * 
   * Clear trigger receives "active" state when no sort is active.
   * 
   * @private
   */
  #updateStates() {
    // Clear all current states
    this.state.clear();
    
    if (this.#activeKey) {
      // Key token (e.g. "sort-bestseller", "sort-name")
      this.state.add(`sort-${this.#activeKey}`);
      // Direction token
      this.state.add(
        this.#activeDirection === 'asc' ? STATES.SORT_ASC : STATES.SORT_DESC
      );
    }
    
    // --- Trigger states ---
    const exactValue = `${this.#activeKey}:${this.#activeDirection}`;
    
    for (const trigger of this.#triggers) {
      const value = trigger.getAttribute(ATTR_VALUE) || '';
      const isExactMatch = value === exactValue;
      const triggerState = this.createState(trigger);
      
      triggerState.remove(STATES.SORT_ASC);
      triggerState.remove(STATES.SORT_DESC);
      triggerState.remove(STATES.ACTIVE);
      
      if (isExactMatch) {
        triggerState.add(STATES.ACTIVE);
        triggerState.add(
          this.#activeDirection === 'asc' ? STATES.SORT_ASC : STATES.SORT_DESC
        );
      }
    }
    
    // --- Clear trigger state ---
    if (this.#clearTrigger) {
      const clearState = this.createState(this.#clearTrigger);
      clearState.remove(STATES.ACTIVE);
      
      if (!this.#activeKey) {
        clearState.add(STATES.ACTIVE);
      }
    }
    
    // --- Label text ---
    if (this.#label) {
      if (!this.#activeKey) {
        this.#label.textContent = this.#label.dataset.default || '';
      } else {
        const match = this.#getActiveMatch();
        this.#label.textContent = this.#resolveLabelText(match);
      }
    }
  }
  
  /**
   * Emit current sort state
   * @private
   */
  #emitCurrentState() {
    this.emit(EVENTS.CHANGED, {
      target: this.#targetId,
      key: this.#activeKey,
      direction: this.#activeDirection
    });
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * public api
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Clear active sort
   */
  clear() {
    this.#activeKey = null;
    this.#activeDirection = 'asc';
    
    // Reset select if present
    if (this.#select) {
      this.#select.value = '';
    }
    
    this.#updateStates();
    this.#emitCurrentState();
  }
  
  /**
   * Set sort programmatically
   * @param {string} key - Sort key (field name)
   * @param {'asc'|'desc'} direction - Sort direction
   */
  setSort(key, direction = 'asc') {
    this.#activeKey = key;
    this.#activeDirection = direction;
    
    // Update select if present
    if (this.#select) {
      const value = direction === 'desc' ? `${key}:desc` : `${key}:asc`;
      const option = Array.from(this.#select.options).find(opt => 
        opt.value === value || opt.value === key
      );
      if (option) {
        this.#select.value = option.value;
      }
    }
    
    this.#updateStates();
    this.#emitCurrentState();
  }
  
  /**
   * Get target list ID
   * @returns {string|null}
   */
  get targetId() {
    return this.#targetId;
  }
  
  /**
   * Get active sort key
   * @returns {string|null}
   */
  get activeKey() {
    return this.#activeKey;
  }
  
  /**
   * Get active sort direction
   * @returns {'asc'|'desc'}
   */
  get activeDirection() {
    return this.#activeDirection;
  }
  
  /**
   * Check if sort is active
   * @returns {boolean}
   */
  get isActive() {
    return this.#activeKey !== null;
  }
}

/* ═══════════════════════════════════════════════════════════════════
 * register web component
 * ═══════════════════════════════════════════════════════════════════ */

customElements.define('liftr-list-sort', LiftrListSort);

export { LiftrListSort, ROLES, STATES, EVENTS, SETTINGS };